<script setup lang="ts">
import { Head, useForm } from '@inertiajs/vue3'
import { UserPlus } from 'lucide-vue-next'

import InputError from '@/components/InputError.vue'
import PasswordInput from '@/components/PasswordInput.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import AppLayout from '@/layouts/AppLayout.vue'
import { newUserPath, usersPath } from '@/routes'
import { type BreadcrumbItem } from '@/types'

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Staff', href: usersPath() },
  { title: 'New Staff', href: newUserPath() },
]

const form = useForm({
  name:                  '',
  email:                 '',
  password:              '',
  password_confirmation: '',
  role:                  '',
})

function submit() {
  form.post(usersPath())
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <Head title="New Staff" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <UserPlus class="text-muted-foreground size-6" />
        <div>
          <h1 class="text-foreground text-xl font-semibold">New Staff</h1>
          <p class="text-muted-foreground text-sm">Create a new internal staff account</p>
        </div>
      </div>

      <!-- Form -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border max-w-lg">
        <CardHeader>
          <CardTitle class="text-base">Account Details</CardTitle>
          <CardDescription>Fill in the details for the new staff member.</CardDescription>
        </CardHeader>
        <CardContent>
          <form class="space-y-5" @submit.prevent="submit">
            <div class="grid gap-2">
              <Label for="name">Name</Label>
              <Input id="name" v-model="form.name" placeholder="Full name" required />
              <InputError :messages="err('name')" />
            </div>

            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="form.email" type="email" placeholder="email@example.com" required />
              <InputError :messages="err('email')" />
            </div>

            <div class="grid gap-2">
              <Label for="role">Role</Label>
              <Select v-model="form.role" required>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="associate">Associate</SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="err('role')" />
            </div>

            <div class="grid gap-2">
              <Label for="password">Password</Label>
              <PasswordInput id="password" v-model="form.password" autocomplete="new-password" required />
              <InputError :messages="err('password')" />
            </div>

            <div class="grid gap-2">
              <Label for="password_confirmation">Confirm Password</Label>
              <PasswordInput id="password_confirmation" v-model="form.password_confirmation" autocomplete="new-password" required />
              <InputError :messages="err('password_confirmation')" />
            </div>

            <div class="flex items-center gap-3 pt-1">
              <Button type="submit" :disabled="form.processing">Create Staff</Button>
              <Button variant="outline" as-child>
                <a :href="usersPath()">Cancel</a>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
